import {
  a,
  button,
  div,
  fnstate,
  h2,
  img,
  input,
  label,
  li,
  option,
  select,
  span,
  textarea,
  ul
} from '@srfnstack/fntags'
import { getAvailableSizes, getVariantIdsForSizes, getSizesFromVariantIds } from '../../utils/variants.js'
import { getHex, getSwatch, isLight } from '../../utils/colors.js'

export const MetadataEditor = ({ product, variants, selectedVariants, productTypes, upscaledAsset, bgRemovedPath, sourceThumbnail, viewerAsset, handleUpdate }) => {
  // Color used as the artwork background preview — initialized to first selected color
  const initialSelectedColors = product().attributes?.selected_colors || []
  const previewColor = fnstate(initialSelectedColors.length > 0 ? getHex(initialSelectedColors[0]) : null)
  // Stable color order: selected first, then unselected — set once on first product load, never re-sorted.
  const colorOrder = fnstate(null)

  // Title and description are edited in place rather than re-derived from
  // `product` on every render. The SEO card used to sit entirely inside
  // product.bindAs, so any background refresh — mockup polling ticks every 4s
  // while a generation is running, plus job WebSocket events — rebuilt these
  // inputs from the server copy and silently discarded whatever had been typed
  // but not yet saved. Seeding once per product keeps an in-progress edit alive.
  const titleLen = fnstate(0)
  const titleInput = input({
    type: 'text',
    class: 'input input-bordered w-full font-medium text-sm',
    oninput: (e) => titleLen(e.target.value.length)
  })
  const descInput = textarea({
    class: 'textarea textarea-bordered w-full h-64 text-sm leading-relaxed'
  })
  let seededForProduct = null
  const seedSeoFields = (p) => {
    if (!p || seededForProduct === p.id) return
    seededForProduct = p.id
    titleInput.value = p.listing_metadata?.title || ''
    descInput.value = p.listing_metadata?.description || ''
    titleLen(titleInput.value.length)
  }
  seedSeoFields(product())
  product.subscribe(seedSeoFields)

  const saveSeo = () => {
    const p = product()
    if (!p) return
    handleUpdate({
      listing_metadata: {
        ...p.listing_metadata,
        title: titleInput.value,
        description: descInput.value
      }
    })
  }

  return (
    div({ class: 'w-full lg:w-96 space-y-6 lg:overflow-y-auto lg:min-h-0' },
    // Integration Status
      product.bindAs(p => {
        if (!p) return div()
        return div({ class: 'card bg-base-100 border border-base-300 shadow-sm overflow-hidden' },
          div({ class: 'p-4 bg-base-200/50 border-b border-base-300' },
            h2({ class: 'font-bold text-xs uppercase tracking-widest opacity-60 flex items-center gap-2' },
              span({ class: 'material-symbols-rounded !text-sm' }, 'hub'),
              'Integration Status'
            )
          ),
          div({ class: 'p-4 space-y-2' },
            p.printify_product_id
              ? div({ class: 'flex justify-between items-center' },
                span({ class: 'text-xs font-bold' }, 'Printify ID'),
                button({ class: 'btn btn-xs btn-link p-0 h-auto min-h-0', onclick: () => window.open(`https://printify.com/app/product-details/${p.printify_product_id}`) }, p.printify_product_id)
              )
              : div({ class: 'text-xs opacity-50' }, 'Not yet pushed to Printify'),
            p.etsy_listing_id
              ? div({ class: 'flex justify-between items-center' },
                span({ class: 'text-xs font-bold' }, 'Etsy ID'),
                button({ class: 'btn btn-xs btn-link p-0 h-auto min-h-0', onclick: () => window.open(`https://www.etsy.com/listing/${p.etsy_listing_id}`) }, p.etsy_listing_id)
              )
              : div({ class: 'text-xs opacity-50' }, 'Not yet published to Etsy')
          )
        )
      }),

      // Printify Variant Selection
      product.bindAs(p => {
        if (!p) return div()
        const isTShirt = p.product_type?.toLowerCase() === 't-shirt'

        if (isTShirt) {
          // T-shirt: show size grid — only sizes available across all selected colors
          return div({ class: 'card bg-base-100 border border-base-300 shadow-sm overflow-hidden' },
            div({ class: 'p-4 bg-base-200/50 border-b border-base-300' },
              h2({ class: 'font-bold text-xs uppercase tracking-widest opacity-60 flex items-center gap-2' },
                span({ class: 'material-symbols-rounded !text-sm' }, 'straighten'),
                'Sizes'
              )
            ),
            div({ class: 'p-4' },
              variants.bindAs(allVariants => {
                const selectedColors = p.attributes?.selected_colors || []
                const availableSizes = getAvailableSizes(allVariants, selectedColors)
                if (availableSizes.length === 0) {
                  return div({ class: 'text-xs opacity-50 text-center py-2' }, 'No sizes found — select shirt colors first.')
                }
                return div({ class: 'space-y-3' },
                  div({ class: 'flex flex-wrap gap-2' },
                    availableSizes.map(size =>
                      selectedVariants.bindAs(sel => {
                        const sizeSel = selectedColors.length > 0
                          ? getVariantIdsForSizes(allVariants, [size], selectedColors)
                          : allVariants.filter(v => v.options?.size === size).map(v => v.id)
                        const isChecked = sizeSel.length > 0 && sizeSel.every(id => sel.includes(id))
                        return label({ class: 'cursor-pointer' },
                          input({
                            type: 'checkbox',
                            class: 'sr-only',
                            checked: isChecked,
                            onchange: (e) => {
                              const cur = selectedVariants()
                              const newSel = e.target.checked
                                ? [...new Set([...cur, ...sizeSel])]
                                : cur.filter(id => !sizeSel.includes(id))
                              selectedVariants(newSel)
                              handleUpdate({ attributes: { ...p.attributes, selected_variants: newSel } }, false)
                            }
                          }),
                          span({
                            class: `inline-flex items-center justify-center min-w-[2.5rem] px-3 py-1.5 rounded-lg border text-xs font-bold transition-all ${
                              isChecked
                                ? 'bg-primary text-primary-content border-primary'
                                : 'bg-base-200 text-base-content/50 border-base-300 hover:border-primary/50'
                            }`
                          }, size)
                        )
                      })
                    )
                  ),
                  selectedVariants.bindAs(sel => {
                    const selectedSizes = getSizesFromVariantIds(allVariants, sel)
                    const variantCount = sel.filter(id => allVariants.some(v => v.id === id)).length
                    return div({ class: 'text-[10px] opacity-40' },
                      `${selectedSizes.length} size${selectedSizes.length !== 1 ? 's' : ''} × ${selectedColors.length} color${selectedColors.length !== 1 ? 's' : ''} = ${variantCount} variant${variantCount !== 1 ? 's' : ''}`
                    )
                  })
                )
              })
            )
          )
        }

        // Default: generic checkbox list (canvas, poster, etc.)
        return div({ class: 'card bg-base-100 border border-base-300 shadow-sm overflow-hidden' },
          div({ class: 'p-4 bg-base-200/50 border-b border-base-300' },
            h2({ class: 'font-bold text-xs uppercase tracking-widest opacity-60 flex items-center gap-2' },
              span({ class: 'material-symbols-rounded !text-sm' }, 'format_list_bulleted'),
              'Printify Variants'
            )
          ),
          div({ class: 'p-2 max-h-64 overflow-y-auto bg-base-200/30' },
            variants.bindAs(list => list.length === 0
              ? div({ class: 'text-xs opacity-50 p-4 text-center' }, 'No variants available for this type.')
              : div({ class: 'grid grid-cols-1 gap-1' },
                list.map(v => label({ class: 'label cursor-pointer justify-start gap-3 hover:bg-base-200 p-2 rounded' },
                  input({
                    type: 'checkbox',
                    class: 'checkbox checkbox-xs checkbox-primary',
                    checked: selectedVariants.bindAttr(sel => sel.includes(v.id)),
                    onchange: (e) => {
                      const sel = selectedVariants()
                      const newSel = e.target.checked ? [...sel, v.id] : sel.filter(id => id !== v.id)
                      selectedVariants(newSel)
                      handleUpdate({ attributes: { ...p.attributes, selected_variants: newSel } }, false)
                    }
                  }),
                  span({ class: 'label-text text-[10px]' }, v.title)
                ))
              )
            )
          )
        )
      }),

      // T-Shirt Color Selection (only for t-shirt product type)
      product.bindAs(p => {
        if (!p || p.product_type?.toLowerCase() !== 't-shirt') return div()
        const availableColors = p.attributes?.available_colors || []
        const selectedColors = p.attributes?.selected_colors || []
        if (availableColors.length === 0) return div()

        // Set stable order once on first load: selected first, then unselected.
        if (colorOrder() === null) {
          colorOrder([
            ...availableColors.filter(c => selectedColors.includes(c)),
            ...availableColors.filter(c => !selectedColors.includes(c))
          ])
        }
        const orderedColors = colorOrder() || availableColors

        return div({ class: 'card bg-base-100 border border-base-300 shadow-sm overflow-hidden' },
          div({ class: 'p-4 bg-base-200/50 border-b border-base-300 flex justify-between items-center' },
            h2({ class: 'font-bold text-xs uppercase tracking-widest opacity-60 flex items-center gap-2' },
              span({ class: 'material-symbols-rounded !text-sm' }, 'palette'),
              'T-Shirt Colors'
            ),
            span({ class: 'badge badge-ghost badge-xs opacity-50' }, `${selectedColors.length} / 5 selected`)
          ),
          div({ class: 'p-2 max-h-64 overflow-y-auto bg-base-200/30' },
            div({ class: 'grid grid-cols-1 gap-1' },
              orderedColors.map(color => {
                const hex = getHex(color)
                const swatch = getSwatch(color)
                const light = isLight(hex)
                const isSelected = selectedColors.includes(color)
                const atLimit = selectedColors.length >= 5 && !isSelected
                return label({ class: `label cursor-pointer justify-start gap-3 hover:bg-base-200 p-2 rounded ${atLimit ? 'opacity-30 pointer-events-none' : ''}` },
                  input({
                    type: 'checkbox',
                    class: 'checkbox checkbox-xs checkbox-primary',
                    checked: isSelected,
                    disabled: atLimit,
                    onchange: (e) => {
                      const newSel = e.target.checked
                        ? [...selectedColors, color]
                        : selectedColors.filter(c => c !== color)
                      // Re-derive selected_variants: keep current sizes but apply new color selection
                      const allVariants = variants()
                      const currentSizes = getSizesFromVariantIds(allVariants, selectedVariants())
                      const sizesToUse = currentSizes.length > 0 ? currentSizes : getAvailableSizes(allVariants, newSel)
                      const newVariantIds = getVariantIdsForSizes(allVariants, sizesToUse, newSel)
                      selectedVariants(newVariantIds)
                      handleUpdate({ attributes: { ...p.attributes, selected_colors: newSel, selected_variants: newVariantIds } }, false)
                    }
                  }),
                  div({
                    class: `w-4 h-4 rounded-full flex-shrink-0 bg-cover ${light ? 'border border-base-content/20' : ''}`,
                    style: `background-color: ${hex}${swatch ? `; background-image: url('${swatch}'); background-blend-mode: multiply` : ''}`
                  }),
                  span({ class: 'label-text text-[10px] flex-1' }, color),
                  previewColor.bindAs(pc => button({
                    class: `btn btn-ghost btn-xs p-0 h-auto min-h-0 ${pc === hex ? 'text-primary' : 'opacity-30 hover:opacity-100'}`,
                    title: 'Preview on artwork',
                    onclick: (e) => { e.preventDefault(); e.stopPropagation(); previewColor(hex) }
                  }, span({ class: 'material-symbols-rounded !text-sm' }, pc === hex ? 'visibility' : 'visibility')))
                )
              })
            )
          )
        )
      }),

      // Printify Print Preview — Printify's own product mockup (the flat print
      // view), captured after push. The ground truth for print placement so you
      // can verify full-bleed/mirror without opening Printify.
      product.bindAs(p => {
        const previewUrl = p && p.attributes && p.attributes.printify_preview_url
        return previewUrl
          ? div({ class: 'card bg-base-100 border border-base-300 shadow-sm overflow-hidden' },
            div({ class: 'p-4 bg-base-200/50 border-b border-base-300' },
              h2({ class: 'font-bold text-xs uppercase tracking-widest opacity-60 flex items-center gap-2' },
                span({ class: 'material-symbols-rounded !text-sm' }, 'print'),
                'Printify Print Preview'
              )
            ),
            div({ class: 'p-4' },
              a({ href: previewUrl, target: '_blank', rel: 'noopener', title: 'Open full size in Printify CDN' },
                img({ src: previewUrl, class: 'w-full object-contain rounded-lg border border-base-300' })
              )
            )
          )
          : div()
      }),

      // Master Artwork
      div({ class: 'card bg-base-100 border border-base-300 shadow-sm overflow-hidden' },
        div({ class: 'p-4 bg-base-200/50 border-b border-base-300' },
          h2({ class: 'font-bold text-xs uppercase tracking-widest opacity-60 flex items-center gap-2' },
            span({ class: 'material-symbols-rounded !text-sm' }, 'image'),
            'Master Artwork'
          )
        ),
        div({ class: 'p-4' },
          div({
            class: 'rounded-lg overflow-hidden cursor-pointer border-2 border-transparent hover:border-primary transition-all relative group bg-base-200',
            style: previewColor.bindAttr(pc => pc ? `background-color: ${pc}` : ''),
            onclick: () => viewerAsset(upscaledAsset)
          },
          (bgRemovedPath || sourceThumbnail) ? img({ src: `/api/image?file=${encodeURIComponent(bgRemovedPath || sourceThumbnail)}`, class: 'w-full object-contain' }) : '',
          div({ class: 'absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity' },
            span({ class: 'text-white text-xs font-bold uppercase tracking-widest' }, 'Zoom View')
          )
          )
        )
      ),

      // Product Configuration
      product.bindAs(p => {
        if (!p) return div()
        return div({ class: 'card bg-base-100 border border-base-300 shadow-sm overflow-hidden' },
          div({ class: 'p-4 bg-base-200/50 border-b border-base-300 flex justify-between items-center' },
            h2({ class: 'font-bold text-xs uppercase tracking-widest opacity-60 flex items-center gap-2' },
              span({ class: 'material-symbols-rounded !text-sm' }, 'inventory_2'),
              'Product Configuration'
            )
          ),
          div({ class: 'p-6 space-y-4' },
            div({ class: 'form-control' },
              label({ class: 'label' },
                span({ class: 'label-text font-bold text-xs uppercase opacity-50' }, 'Target Product Type'),
                p.mockups && p.mockups.length > 0 ? span({ class: 'badge badge-ghost badge-xs opacity-50' }, 'Locked: Mockups Generated') : ''
              ),
              select({
                class: 'select select-bordered w-full',
                onchange: (e) => handleUpdate({ product_type: e.target.value }),
                disabled: p.mockups && p.mockups.length > 0
              },
              productTypes().map(t => option({
                value: t.keyword,
                selected: p.product_type.toLowerCase() === t.keyword.toLowerCase()
              }, t.keyword))
              )
            ),
            // "No framed sibling": stops sibling creation for a design whose
            // frame is painted into the art. Shown only for a product type
            // that has siblings; bound to productTypes because the type list
            // loads after the product.
            productTypes.bindAs(types => {
              const hasSiblingType = types.some(t => t.sibling_of && t.sibling_of.toLowerCase() === p.product_type.toLowerCase())
              if (!hasSiblingType) return span()
              return div({ class: 'form-control' },
                label({ class: 'label cursor-pointer justify-start gap-3' },
                  input({
                    type: 'checkbox',
                    class: 'toggle toggle-sm',
                    checked: !!p.no_sibling,
                    onchange: (e) => handleUpdate({ no_sibling: e.target.checked }, false)
                  }),
                  span({ class: 'label-text font-bold text-xs uppercase opacity-50' }, 'No framed sibling')
                ),
                span({ class: 'label-text-alt opacity-40 text-xs' }, 'For designs with a frame painted into the art')
              )
            })
          )
        )
      }),

      // SEO Metadata. The card shell and the two free-text inputs are built once
      // and never re-rendered, so a background product refresh can't wipe an
      // unsaved edit. Only the tag list — which is edited through `product`
      // itself — stays bound.
      div({ class: 'card bg-base-100 border border-base-300 shadow-sm overflow-hidden' },
        div({ class: 'p-4 bg-base-200/50 border-b border-base-300 flex justify-between items-center' },
          h2({ class: 'font-bold text-xs uppercase tracking-widest opacity-60 flex items-center gap-2' },
            span({ class: 'material-symbols-rounded !text-sm' }, 'analytics'),
            'SEO Metadata'
          ),
          button({ class: 'btn btn-ghost btn-xs text-primary', onclick: saveSeo }, 'Save SEO')
        ),
        div({ class: 'p-6 space-y-4' },
          div({ class: 'form-control' },
            label({ class: 'label' }, span({ class: 'label-text font-bold text-xs uppercase opacity-50' }, 'Listing Title')),
            titleInput,
            label({ class: 'label' }, titleLen.bindAs(n => span({ class: 'label-text-alt opacity-40' }, `${n} / 140 chars`)))
          ),
          div({ class: 'form-control' },
            label({ class: 'label' }, span({ class: 'label-text font-bold text-xs uppercase opacity-50' }, 'Description')),
            descInput
          ),
          div({ class: 'form-control' },
            label({ class: 'label' }, span({ class: 'label-text font-bold text-xs uppercase opacity-50' }, 'Search Tags')),
            product.bindAs(p => div({ class: 'max-h-48 overflow-y-auto border border-base-300 rounded-lg bg-base-200/30 p-2' },
              ul({ class: 'space-y-1' },
                (p?.listing_metadata?.tags || []).map(tag => li({ class: 'flex justify-between items-center bg-base-100 px-2 py-1 rounded border border-base-300 text-xs' },
                  span(tag),
                  button({
                    class: 'btn btn-ghost btn-xs text-error h-auto min-h-0 p-0 px-1',
                    onclick: () => {
                      p.listing_metadata.tags = p.listing_metadata.tags.filter(t => t !== tag)
                      product({ ...p })
                    }
                  }, '×')
                ))
              ),
              button({
                class: 'btn btn-ghost btn-xs w-full mt-2 text-primary',
                onclick: async () => {
                  const newTag = await window.prompt('Add new tag:')
                  if (newTag) {
                    const cur = product()
                    cur.listing_metadata.tags = [...(cur.listing_metadata.tags || []), newTag]
                    product({ ...cur })
                  }
                }
              }, '+ Add Tag')
            ))
          )
        )
      )
    )
  )
}
